<template>
  <BaseWidget
    title="Upcoming tours"
    :show-info-icon="true"
    info-tooltip="Tours created by the tour booking form"
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/4088442-how-do-i-enable-leads-to-book-tours-from-my-website', text: 'Learn about Tours' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    custom-class="upcoming-tours-widget"
    :date-range="dateRange"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      Error loading tours: {{ error.message }}
    </div>
    <template v-else>
      <div
        ref="widgetRef"
        class="widget-content-wrapper"
        :class="{ 'focused': focused }"
      >
        <div class="tour-count">
          <div class="count-row">
            <span class="count">{{ tourCount }}</span>
            <ComparisonChip 
              :change="changePercentage" 
              :previous-value="formattedPreviousCount"
              :previous-period-dates="previousTimestamps"
              :date-range-preset="dateRange"
              label="Upcoming tours"
            />
          </div>
          <div class="count-label-row">
            <span class="count-label">{{ dateRangeLabel }}</span>
            <a
              v-if="tours.length > 0"
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </div>
        
        <div
          v-if="!focused"
          class="tours-list"
        >
          <div 
            v-for="(tour, index) in displayTours" 
            :key="tour.id || index" 
            class="tour-item"
            @click="handleTourClickGuarded(tour)"
          >
            <div class="tour-row">
              <div class="tour-image">
                <img 
                  v-if="tour.image" 
                  :src="tour.image" 
                  :alt="tour.name"
                >
                <div
                  v-else
                  class="image-placeholder"
                >
                  {{ getInitials(tour.name) }}
                </div>
              </div>
              <div class="tour-details">
                <div class="tour-name">
                  {{ tour.name }}
                </div>
                <div class="tour-meta">
                  <span class="tour-time">{{ formatTourTime(tour) }}</span>
                  <span
                    v-if="tour.user?.name"
                    class="tour-meta-sep"
                  > • </span>
                  <span
                    v-if="tour.user?.name"
                    class="tour-account-name"
                  >{{ tour.user.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="displayTours.length === 0 && tours.length === 0 && !hasMoreTours"
            class="no-tours"
          >
            No upcoming tours
          </div>
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch, onMounted } from 'vue'
import BaseWidget from './BaseWidget.vue'

import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps, DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRangeDates } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { executeGraphQLQuery, getOrganizationDetails } from '../services/optixApi.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'
import { dataFetcher } from '../services/dataFetcher.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { getResourceImageUrl } from '../utils/resourceImage.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { getDemoUpcomingToursData } from '../services/demoData.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'next_7_days')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Next 7 days'
})

// Get location IDs from parent window URL
const locationIds = useLocationFilter()

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Calculate timestamps from date range
const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
})

// Query tours only (type: TOUR) with full details for display, limited to 3 items
const toursQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: TOUR
      status: [ACTIVE, UPCOMING]
    ) {
      total
      data {
        tour_id
        title
        start_timestamp
        end_timestamp
        resource {
          resource_id
          name
          main_image {
            url
            __typename
          }
          location {
            location_id
            name
            timezone
            timezone_name
            __typename
          }
          __typename
        }
        location {
          location_id
          name
          timezone
          timezone_name
          __typename
        }
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'tours_display',
  query: toursQuery,
  variables: computed(() => ({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp,
    limit: 3,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoUpcomingToursData({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp
  })
})

// Organization ID - try URL first, then fetch from API
const organizationId = ref(null)

// Fetch organization ID from API if not in URL
const fetchOrganizationId = async () => {
  const urlOrgId = getOrganizationIdFromURL()
  if (urlOrgId) {
    organizationId.value = urlOrgId
    return
  }
  
  try {
    const result = await getOrganizationDetails()
    const orgId = result?.data?.organization?.organization_id || result?.organization?.organization_id
    if (orgId) {
      organizationId.value = orgId
    }
  } catch (err) {
    console.error('Failed to fetch organization ID:', err)
  }
}

// Fetch organization ID immediately (don't wait for mount)
fetchOrganizationId()

// Fetch locations with their images
const locationsQuery = `
  query GetAllLocations($organizationId: ID!) {
    locations(
      organization_id: $organizationId
      include_visible: true
      include_hidden: true
    ) {
      total
      data {
        location_id
        main_image { 
          url 
        }
      }
    }
  }
`

// Use useWidgetData directly with custom fetch function to handle null orgId
const locationsData = ref(null)
const locationsLoading = ref(false)
const locationsError = ref(null)

const fetchLocations = async () => {
  if (!organizationId.value) {
    return // Skip if organization ID is not available
  }
  
  try {
    locationsLoading.value = true
    locationsError.value = null
    
    const result = await dataFetcher.fetchGraphQL(
      locationsQuery,
      { organizationId: organizationId.value },
      'GetAllLocations',
      1,
      true
    )

    locationsData.value = result
  } catch (err) {
    console.error('Error fetching locations:', err)
    locationsError.value = err
    locationsData.value = null
  } finally {
    locationsLoading.value = false
  }
}

// Watch for organization ID to become available and fetch locations
watch(organizationId, (newOrgId) => {
  if (newOrgId) {
    fetchLocations()
  }
}, { immediate: true })

const locationsCleanup = () => {
  // Cleanup if needed
}

// Create a map of location_id to location image URL
const locationImageMap = computed(() => {
  if (!locationsData.value?.locations?.data) {
    return {}
  }
  const map = {}
  locationsData.value.locations.data.forEach(location => {
    if (location.location_id && location.main_image?.url) {
      map[location.location_id] = location.main_image.url
    }
  })
  return map
})

// Fetch previous period data for comparison
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
})

const previousQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: TOUR
      status: [ACTIVE, UPCOMING]
    ) {
      total
      __typename
    }
  }
`

const {
  data: previousData,
  loading: previousLoading,
  error: previousError,
  cleanup: previousCleanup,
  refresh: refreshPrevious
} = useGraphQLData({
  dataKey: 'tours_previous',
  query: previousQuery,
  variables: computed(() => ({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp,
    limit: 1,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoUpcomingToursData({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp
  })
})

// Ensure previous data is fetched when current data loads
watch(() => data.value, (newData) => {
  if (newData && !previousLoading.value && !previousData.value) {
    refreshPrevious(true)
  }
}, { immediate: true })

// Map schedule data to display format (API already filters by type: TOUR)
const tours = computed(() => {
  if (!data.value?.schedule?.data) {
    return []
  }
  return data.value.schedule.data
    .map(item => {
      const locationId = item.location?.location_id
      const locationImage = locationId ? locationImageMap.value[locationId] : null
      
      // Get timezone from location (prefer direct location, fallback to resource location)
      const location = item.location || item.resource?.location
      const timezone = location?.timezone || null
      
      return {
        id: item.tour_id,
        name: item.title || item.resource?.name || item.location?.name || 'Unnamed Tour',
        time: item.start_timestamp * 1000, // Convert to milliseconds
        endTime: item.end_timestamp * 1000,
        image: locationImage || getResourceImageUrl(null, item.resource?.name || item.title || 'Tour'),
        location: location?.name || null,
        timezone: timezone, // Include timezone for formatting
        user: item.owner_account ? {
          name: item.owner_account.name || null,
          avatar: getAvatarUrl(item.owner_account.avatar?.url, item.owner_account.name || item.owner_account.account_id)
        } : null
      }
    })
    .sort((a, b) => a.time - b.time) // Sort by start time
})

// Count tours in the selected date range
const tourCount = computed(() => {
  return data.value?.schedule?.total ?? tours.value.length
})

// Count tours in previous period
const previousCount = computed(() => {
  // Only return 0 if data has loaded but is empty
  // Return null if data hasn't loaded yet
  if (previousData.value === null || previousData.value === undefined) return null
  return previousData.value?.schedule?.total ?? 0
})

// Calculate percentage change
const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  const current = tourCount.value
  const previous = previousCount.value
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change
  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10 // Round to 1 decimal place
})

// Format previous count for display
const formattedPreviousCount = computed(() => {
  if (previousCount.value === null || previousCount.value === undefined) return null
  return previousCount.value.toString()
})

// Calculate number of rows to display based on widget height
// Calculate number of rows to display based on widget height (64px rows; leave room for "View more")
// Pattern: 2-row→2 slots, 3-row→4, 4-row→6, 5-row→8, 6-row→10... i.e. 2*(rowSpan-1)
const maxRowsToDisplay = computed(() => {
  if (props.rowSpan === 1) return 0
  return 2 * (props.rowSpan - 1)
})

// Check if there are more tours than we can display (only show when rowSpan > 1)
const hasMoreTours = computed(() => {
  return props.rowSpan > 1 && tourCount.value > maxRowsToDisplay.value
})

// Calculate remaining count
const remainingToursCount = computed(() => {
  if (!hasMoreTours.value) return 0
  return tourCount.value - displayTours.value.length
})

// Display upcoming tours based on widget height (leave room for "more" row if needed)
const displayTours = computed(() => {
  if (maxRowsToDisplay.value === 0) {
    return []
  }
  return tours.value.slice(0, maxRowsToDisplay.value)
})

// Format tour time for display using the location's timezone
const formatTourTime = (tour) => {
  if (!tour.time) return ''
  
  // Use the location's timezone if available and valid, otherwise fall back to browser timezone
  const timezone = (tour.timezone && typeof tour.timezone === 'string' && tour.timezone.trim()) || undefined
  
  // Get user's locale timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  
  // Create date formatters with the location's timezone
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  })
  
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  })
  
  const dateFormatterForComparison = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone
  })
  
  const tourDate = new Date(tour.time)
  
  // Only show timezone when it differs from user's. Compare by UTC offset at this date (reliable;
  // abbreviation can vary: same zone may show "PST" vs "GMT-8" and falsely differ)
  let showTimezoneIndicator = false
  let timezoneFormatter = null
  if (timezone) {
    try {
      const locParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortOffset' }).formatToParts(tourDate)
      const userParts = new Intl.DateTimeFormat('en-US', { timeZone: userTimezone, timeZoneName: 'shortOffset' }).formatToParts(tourDate)
      const locOffset = locParts.find(p => p.type === 'timeZoneName')?.value || ''
      const userOffset = userParts.find(p => p.type === 'timeZoneName')?.value || ''
      showTimezoneIndicator = locOffset !== userOffset
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    } catch (_) {
      showTimezoneIndicator = timezone !== userTimezone
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    }
  }
  
  // Get date parts in the location's timezone for comparison
  const tourDateParts = dateFormatterForComparison.formatToParts(tourDate)
  const tourYear = parseInt(tourDateParts.find(p => p.type === 'year').value)
  const tourMonth = parseInt(tourDateParts.find(p => p.type === 'month').value) - 1 // Month is 0-indexed
  const tourDay = parseInt(tourDateParts.find(p => p.type === 'day').value)
  
  // Get today's date in the location's timezone
  const now = new Date()
  const todayParts = dateFormatterForComparison.formatToParts(now)
  const todayYear = parseInt(todayParts.find(p => p.type === 'year').value)
  const todayMonth = parseInt(todayParts.find(p => p.type === 'month').value) - 1
  const todayDay = parseInt(todayParts.find(p => p.type === 'day').value)
  
  const tourDateObj = new Date(tourYear, tourMonth, tourDay)
  const todayDateObj = new Date(todayYear, todayMonth, todayDay)
  
  const diffTime = tourDateObj - todayDateObj
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  // Format time using the location's timezone
  let timeStr = timeFormatter.format(tourDate)
  
  // Add timezone indicator if different from user's timezone
  if (showTimezoneIndicator && timezoneFormatter) {
    const timezoneParts = timezoneFormatter.formatToParts(tourDate)
    const timezoneName = timezoneParts.find(p => p.type === 'timeZoneName')?.value || ''
    if (timezoneName) {
      timeStr = `${timeStr} (${timezoneName})`
    }
  }
  
  if (diffDays === 0) {
    return `Today @ ${timeStr}`
  } else if (diffDays === 1) {
    return `Tomorrow @ ${timeStr}`
  } else {
    const dateStr = dateFormatter.format(tourDate)
    return `${dateStr} @ ${timeStr}`
  }
}

// Get initials for placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// GraphQL query to fetch tour web_link
const TOUR_QUERY = `
  query GetTourWebLink($tour_id: ID!) {
    tour(tour_id: $tour_id) {
      web_link
    }
  }
`

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || '',
    token: urlParams.get('token') || urlParams.get('access_token') || '',
    organization_id: urlParams.get('orgId') || urlParams.get('organization_id') || '',
    subdomain: urlParams.get('subdomain') || '',
    locations: urlParams.get('locations') || '',
    email: urlParams.get('email') || '',
    current_user_id: urlParams.get('current_user_id') || urlParams.get('userId') || '',
    optix_placement: urlParams.get('optix-placement') || urlParams.get('optix_placement') || 'float',
    optix_mobile: urlParams.get('optix-mobile') || urlParams.get('optix_mobile') || 'false'
  }
}

// Handle "View all" click - navigate to schedule list page via parent window
// Uses same method as UpcomingBookingsWidget (ReplaceUrl postMessage)
const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const params = getUrlParams()

  // Try to get client_id and canvasUUID from various sources
  let clientId = params.client_id
  let canvasUUID = params.canvasUUID

  // Check document.referrer for parameters
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      const referrerParams = new URLSearchParams(referrerUrl.search)
      if (!clientId) {
        clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      }
      if (!canvasUUID) {
        canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
      if (clientId || canvasUUID) {
      }
    } catch (e) {
    }
  }

  // Try to get from parent window's URL if accessible (may fail due to CORS)
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = window.parent.location.href
        const parentUrlObj = new URL(parentUrl)
        const parentParams = new URLSearchParams(parentUrlObj.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      }
    } catch (e) {
      // CORS - can't access parent location
    }
  }

  // Set up listener to see if parent responds or sends any messages
  const messageListener = (event) => {
    if (event.data && typeof event.data === 'object') {
    }
  }
  window.addEventListener('message', messageListener)

  // Remove listener after 10 seconds
  setTimeout(() => {
    window.removeEventListener('message', messageListener)
  }, 10000)

  // Tours schedule list page
  const dates = getDateRangeDates(dateRange.value)
  const navigationUrl = `/schedule/list?event_types=TOUR&date_from=${dates.from}&date_to=${dates.to}`

  // canvasUUID is required and must be non-empty (can be any string)
  const finalCanvasUUID = canvasUUID || "default-canvas-uuid"


  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: clientId || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }


  try {
    window.parent.postMessage(message, "*")

    // Also try window.top in case of nested iframes
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (error) {
    console.error('❌ Error posting message:', error)
  }

}

// Handle tour click - fetch web_link and open in new tab
const handleTourClickGuarded = guard(async (tour) => {
  
  // Validate tour_id exists
  if (!tour.id) {
    console.error('⚠️ Tour ID not found:', tour)
    if (process.env.NODE_ENV === 'development') {
      alert('Error: Tour ID is missing')
    }
    return
  }
  
  const tourId = tour.id
  
  try {
    
    // Fetch the web_link using GraphQL query with network-only (bypass cache)
    // Use executeGraphQLQuery directly to ensure fresh data
    const result = await executeGraphQLQuery(
      TOUR_QUERY,
      { tour_id: tourId },
      'GetTourWebLink'
    )
    
    // Extract data from response
    const data = result.data?.data || result.data
    
    // Check if web_link exists
    if (data?.tour?.web_link) {
      const webLink = data.tour.web_link
      
      // Open the link in a new tab
      window.open(webLink, '_blank')
    } else {
      console.error('⚠️ Tour web_link not found in response:', data)
      if (process.env.NODE_ENV === 'development') {
        alert(`Error: Tour web_link not found for tour ${tourId}`)
      }
    }
  } catch (error) {
    console.error('❌ Error fetching tour web_link:', error)
    if (process.env.NODE_ENV === 'development') {
      alert(`Error fetching tour link: ${error.message}`)
    }
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

onMounted(async () => {
  // Fetch organization ID if not already fetched
  if (!organizationId.value) {
    await fetchOrganizationId()
  }
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
  locationsCleanup()
})
</script>

<style scoped>
:deep(.upcoming-tours-widget) {
  cursor: pointer;
  overflow: hidden;
}

:deep(.upcoming-tours-widget .widget-content) {
  justify-content: flex-start;
  overflow: hidden;
}


.tour-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-bottom: 10px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.widget-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.tours-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.tour-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.tour-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.tour-item:last-child {
  border-bottom: none;
}

.tour-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.tour-item-more:hover {
  background-color: transparent;
}

.tour-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.tour-image {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.tour-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.tour-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.tour-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.tour-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tour-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 4px;
}

.tour-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.tour-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.tour-location {
  font-size: 11px;
  color: #999;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-tours {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.tour-item-more .tour-row {
  width: 100%;
}

.tour-item-more .tour-row-more {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.tour-item-more .view-more-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.tour-item-more .view-more-link:hover {
  color: #2563eb;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.tours-widget-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  flex-shrink: 0;
  padding-top: 4px;
}

.tour-item-more .tour-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.tour-item-more .tour-name {
  margin-bottom: 0;
}

.tour-image-more {
  position: relative;
  width: 36px;
  height: 28px;
  background: transparent;
}

.stacked-images {
  position: relative;
  width: 100%;
  height: 100%;
}

.stacked-image {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid white;
}

.stacked-image-top {
  top: 0;
  left: 0;
  z-index: 2;
  transform: rotate(-8deg);
}

.stacked-image-bottom {
  top: 0;
  left: 7px;
  z-index: 1;
}

.stacked-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>
